import { getAuditTrailData, extractVersionStructure } from '../../../../util/getAuditTrailData';
import { generateNodeIndexing } from '../../../../hooks/useNodeIndexing';
import { generateTitlesFromStructure, uploadJsonToS3 } from '../../../../util/AppUtils';

/**
 * Helper function to ensure all arrays in the cloned structure are mutable
 * Recursively replaces arrays with new mutable copies
 */
const ensureArraysAreMutable = (obj) => {
  if (obj === null || obj === undefined) return obj;
  
  if (Array.isArray(obj)) {
    const newArray = [];
    for (let i = 0; i < obj.length; i++) {
      newArray[i] = ensureArraysAreMutable(obj[i]);
    }
    return newArray;
  }
  
  if (obj && typeof obj === 'object') {
    const result = Object.prototype.toString.call(obj) === '[object Object]' 
      ? {} 
      : Object.create(Object.getPrototypeOf(obj));
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = obj[key];
        result[key] = (value && typeof value === 'object') 
          ? ensureArraysAreMutable(value) 
          : value;
      }
    }
    return result;
  }
  return obj;
};

const setSiblingOrder = (node, index) => {
  if (!node || index === undefined || index === null) return;
  const order = index.toString();
  node.sibling_order = order;
  if (node.procedure_tree && typeof node.procedure_tree === 'object') {
    node.procedure_tree.sibling_order = order;
  }
};

const normalizeChildOrders = (children = []) => {
  if (!Array.isArray(children)) return;
  children.forEach((child, childIndex) => setSiblingOrder(child, childIndex));
};

const normalizeDocumentSiblingOrders = (sections = []) => {
  if (!Array.isArray(sections)) return;

  sections.forEach((section, sectionIndex) => {
    if (!section) return;
    setSiblingOrder(section, sectionIndex);

    if (Array.isArray(section.steps)) {
      section.steps.forEach((step, stepIndex) => {
        setSiblingOrder(step, stepIndex);
        if (Array.isArray(step.children)) normalizeChildOrders(step.children);
        if (Array.isArray(step.substeps)) {
          step.substeps.forEach((substep, substepIndex) => {
            setSiblingOrder(substep, substepIndex);
            if (Array.isArray(substep.children)) normalizeChildOrders(substep.children);
          });
        }
      });
    }

    if (Array.isArray(section.children)) normalizeChildOrders(section.children);
  });
};

const cloneStructureForIndexing = (structure = []) => {
  if (!Array.isArray(structure)) return [];
  return ensureArraysAreMutable(structuredClone(structure));
};

const setBlankStep = (structure = [], sourceStep) => {
  if (!sourceStep || !Array.isArray(structure)) return structure || [];
  const stepId = sourceStep.tree_node_id || sourceStep.step_id_ref;
  if (!stepId) return structure;
  structure.forEach((section) => {
    if (!section || !Array.isArray(section.steps)) return;
    const step = section?.steps?.find(step =>step?.tree_node_id === stepId || step?.step_id_ref === stepId);  
    if(step){
      step.children = [];
      step.substeps = [];
    }
  });

  // normalizeDocumentSiblingOrders(structure);
  return structure;
};

const CHILD_NODE_ID_FIELDS = [
  'attachment_tree_node_id',
  'video_tree_node_id',
  'rich_text_node_id',
  'canvas_tree_node_id',
  'form_tree_node_id',
];

const CHILD_TYPE_MAP = {
  attachment: 'Attachment',
  canvas: 'Canvas',
  'rich-text': 'Rich Text',
  video: 'Video',
  form: 'Form',
};

const getSiblingOrderNumber = (node) => {
  if (!node) return 0;
  const order = node.sibling_order ?? 
                node.procedure_tree?.sibling_order ?? 
                node.detail?.procedure_tree?.sibling_order ?? 
                '0';
  const parsed = parseInt(order, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const getChildNodeId = (child) => {
  if (!child) return null;
  if (child.tree_node_id) return child.tree_node_id;
  const detail = child.detail || {};
  for (const field of CHILD_NODE_ID_FIELDS) {
    if (detail[field]) return detail[field];
  }
  return null;
};

const normalizeChildType = (type = '') => {
  const lowerType = type.toLowerCase();
  if (lowerType === 'canva') return 'canvas';
  if (lowerType === 'richtext') return 'rich-text';
  return lowerType;
};

const getChildTypeDisplayName = (type = '') => {
  const normalized = normalizeChildType(type);
  return CHILD_TYPE_MAP[normalized] || 
         (normalized ? normalized.charAt(0).toUpperCase() + normalized.slice(1) : 'Element');
};

const toLowerCaseFirst = (value) => {
  if (!value) return value;
  return value.charAt(0).toLowerCase() + value.slice(1);
};

const collectStepHierarchyNodes = (step) => {
  const nodes = [];
  if (!step) return nodes;
  
  const stepId = step.tree_node_id || step.step_id_ref;
  if (stepId) {
    nodes.push({ id: step.tree_node_id || stepId, node: step, type: 'step' });
  }

  const sortedStepChildren = [...(step.children || [])].sort(
    (a, b) => getSiblingOrderNumber(a) - getSiblingOrderNumber(b)
  );
  sortedStepChildren.forEach((child) => {
    if (child?.is_deleted) return;
    const childId = getChildNodeId(child);
    if (!childId) return;
    nodes.push({ id: childId, node: child, type: 'stepChild', parentStepId: step.tree_node_id || stepId });
  });

  const sortedSubsteps = [...(step.substeps || [])].sort(
    (a, b) => getSiblingOrderNumber(a) - getSiblingOrderNumber(b)
  );
  sortedSubsteps.forEach((substep) => {
    if (!substep || substep.is_deleted) return;
    const substepId = substep.tree_node_id || substep.substep_id_ref;
    if (substepId) {
      nodes.push({ id: substep.tree_node_id || substepId, node: substep, type: 'substep', parentStepId: step.tree_node_id || stepId });
    }

    const sortedSubstepChildren = [...(substep.children || [])].sort(
      (a, b) => getSiblingOrderNumber(a) - getSiblingOrderNumber(b)
    );
    sortedSubstepChildren.forEach((child) => {
      if (child?.is_deleted) return;
      const childId = getChildNodeId(child);
      if (!childId) return;
      nodes.push({ id: childId, node: child, type: 'substepChild', parentSubstepId: substep.tree_node_id || substepId, parentStepId: step.tree_node_id || stepId });
    });
  });
  
  return nodes;
};

const resolveNodeNameWithFallback = (id, originalIndexMap, node, defaultLabel) => {
  if (id && originalIndexMap?.[id]) return originalIndexMap[id];
  
  if (node) {
    const possibleFields = ['title', 'name', 'step_name', 'stepTitle', 'substep_name', 'label', 'display_name'];
    for (const field of possibleFields) {
      if (node[field]) return node[field];
    }
    
    if (node.detail) {
      const detailFields = ['title', 'name', 'label', 'display_name'];
      for (const field of detailFields) {
        if (node.detail[field]) return node.detail[field];
      }
    }
  }
  return defaultLabel;
};

const formatDestinationName = ({ type, finalSectionName, finalStepName, finalIndexMap, parentSubstepId }) => {
  if (type === 'step') return finalSectionName;
  if (type === 'stepChild' || type === 'substep') return toLowerCaseFirst(finalStepName);
  if (type === 'substepChild') {
    return (parentSubstepId && finalIndexMap?.[parentSubstepId]) 
      ? finalIndexMap[parentSubstepId] 
      : toLowerCaseFirst(finalStepName);
  }
  return finalSectionName;
};

const stripUnaddedChildrenForStepCopy = (node, type) => {
  if (!node) return null;
  const cloned = JSON.parse(JSON.stringify(node));
  if (type === 'step') {
    delete cloned.children;
    delete cloned.substeps;
  } else if (type === 'substep') {
    delete cloned.children;
  }
  return cloned;
};

const findSectionInStructure = (structure = [], sectionId) => {
  if (!sectionId) return null;
  return structure.find(section =>
    section?.tree_node_id === sectionId || section?.section_id_ref === sectionId
  ) || null;
};

const findStepInStructure = (structure = [], stepId) => {
  if (!stepId) return null;
  for (const section of structure) {
    const step = section?.steps?.find(s =>
      s?.tree_node_id === stepId || s?.step_id_ref === stepId
    );
    if (step) return { section, step };
  }
  return null;
};

const findSubstepInStep = (step, substepId) => {
  if (!step || !substepId) return null;
  return step.substeps?.find(substep =>
    substep?.tree_node_id === substepId || substep?.substep_id_ref === substepId
  ) || null;
};

const determineStepInsertIndex = (steps = [], position, movePosition) => {
  if (!Array.isArray(steps) || steps.length === 0 || position === 'into') {
    return steps?.length || 0;
  }

  const targetId = movePosition?.targetId;
  if (!targetId) return steps.length;

  const targetIndex = steps.findIndex(step =>
    step?.tree_node_id === targetId || step?.step_id_ref === targetId
  );

  if (targetIndex === -1) return steps.length;
  return position === 'above' ? targetIndex : targetIndex + 1;
};

const addNodeToStepStructure = ({
  currentStructure = [],
  hierarchyNode,
  finalSection,
  finalStep,
  position,
  movePosition,
  isCopyOperation = false,
  sourceStep,
  newCopiedStepTreeNodeId = null,
}) => {
  const clonedStructure = JSON.parse(JSON.stringify(currentStructure || []));
  const sectionId = finalSection?.tree_node_id || finalSection?.section_id_ref;
  if (!sectionId) return clonedStructure;

  let targetSection = findSectionInStructure(clonedStructure, sectionId);
  if (!targetSection) {
    const sectionSkeleton = {
      tree_node_id: finalSection?.tree_node_id || null,
      section_id_ref: finalSection?.section_id_ref || null,
      steps: [],
    };
    clonedStructure.push(sectionSkeleton);
    normalizeDocumentSiblingOrders(clonedStructure);
    targetSection = sectionSkeleton;
  }

  const { type, parentStepId, parentSubstepId, id } = hierarchyNode;
  const nodeClone = stripUnaddedChildrenForStepCopy(hierarchyNode.node, type);
  if (!nodeClone) return clonedStructure;
  // For copy operations, preserve the original tree_node_id as previous_tree_node_id for non-step types
  // (step type is handled separately below with its own dummy tree_node_id logic)
  if (isCopyOperation && type !== 'step' && nodeClone.tree_node_id) {
    nodeClone.previous_tree_node_id = nodeClone.tree_node_id;
  }

  // Helper to find the correct step (copied step for copy operations, original for move operations)
  const findTargetStep = (stepId) => {
    if (!stepId) return null;
    
    if (isCopyOperation) {
      // For copy operations, we need to find the copied step (with dummy tree_node_id)
      // If we have the tracked newCopiedStepTreeNodeId, use it directly
      if (newCopiedStepTreeNodeId) {
        const copiedStep = targetSection.steps?.find(step =>
          step.tree_node_id === newCopiedStepTreeNodeId
        );
        if (copiedStep) return copiedStep;
      }
      
      // Fallback: try to find the step with dummy tree_node_id by matching step_id_ref
      if (sourceStep) {
        const sourceStepIdRef = sourceStep.step_id_ref;
        const stepWithDummyId = targetSection.steps?.find(step =>
          step.step_id_ref === sourceStepIdRef &&
          step.tree_node_id && step.tree_node_id.startsWith('copy-')
        );
        if (stepWithDummyId) return stepWithDummyId;
      }
      
      // If not found by step_id_ref, try matching by the stepId (which could be tree_node_id or step_id_ref)
      // But prioritize steps with dummy tree_node_id
      const matchingSteps = targetSection.steps?.filter(step => {
        const matchesById = step.tree_node_id === stepId || step.step_id_ref === stepId;
        return matchesById;
      }) || [];
      
      if (matchingSteps.length > 0) {
        // Prioritize the one with dummy tree_node_id (the copy)
        const copiedStep = matchingSteps.find(step => 
          step.tree_node_id && step.tree_node_id.startsWith('copy-')
        );
        if (copiedStep) return copiedStep;
        
        // If multiple matches, return the one with highest sibling_order (likely the copy)
        if (matchingSteps.length > 1) {
          const sorted = [...matchingSteps].sort((a, b) => {
            const orderA = getSiblingOrderNumber(a);
            const orderB = getSiblingOrderNumber(b);
            return orderB - orderA; // Descending - get the one with highest order (the copy)
          });
          return sorted[0];
        }
        
        // If only one match, check if it's the copy (has dummy tree_node_id)
        const candidate = matchingSteps[0];
        if (candidate.tree_node_id && candidate.tree_node_id.startsWith('copy-')) {
          return candidate;
        }
        // Otherwise, it's the original step - don't use it for copy operations
        return null;
      }
    }
    
    // For move operations or if not found, use standard lookup
    const match = findStepInStructure([targetSection], stepId) || findStepInStructure(clonedStructure, stepId);
    return match?.step || null;
  };

  if (type === 'step') {
    if (!Array.isArray(targetSection.steps)) targetSection.steps = [];
    
    // For copy operations, assign a dummy unique tree_node_id to track the new copied step
    if (isCopyOperation && nodeClone) {
      // Generate a dummy unique tree_node_id (using timestamp + random to ensure uniqueness)
      nodeClone.previous_tree_node_id = nodeClone.tree_node_id;
      nodeClone.tree_node_id = `copy-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      // Ensure the step is empty (no children, no substeps)
      delete nodeClone.children;
      delete nodeClone.substeps;
    }
    
    // Check if step already exists to avoid duplicates
    const sourceStepId = sourceStep?.tree_node_id || sourceStep?.step_id_ref;
    const existingStepIndex = targetSection.steps.findIndex(step => {
      if (isCopyOperation) {
        // For copy operations, check if this is the new copied step (has dummy tree_node_id)
        return step.tree_node_id && step.tree_node_id.startsWith('copy-') &&
               (step.step_id_ref === sourceStepId || step.step_id_ref === (nodeClone?.step_id_ref));
      }
      return step.tree_node_id === sourceStepId || step.step_id_ref === sourceStepId;
    });
    
    if (existingStepIndex === -1) {
      const insertIndex = determineStepInsertIndex(targetSection.steps, position, movePosition);
      targetSection.steps.splice(insertIndex, 0, ensureArraysAreMutable(nodeClone));
      targetSection.steps.forEach((step, index) => setSiblingOrder(step, index));
    } else {
      // Step already exists, ensure it has no children/substeps (shouldn't happen in normal flow, but handle it)
      const existingStep = targetSection.steps[existingStepIndex];
      if (type === 'step') {
        delete existingStep.children;
        delete existingStep.substeps;
      }
    }
  } else if (type === 'stepChild') {
    const parentStep = findTargetStep(parentStepId);
    if (parentStep) {
      if (!Array.isArray(parentStep.children)) parentStep.children = [];
      // Check if child already exists to avoid duplicates
      const existingChild = parentStep.children.find(child => {
        const childId = getChildNodeId(child);
        return childId === id;
      });
      if (!existingChild) {
        parentStep.children.push(ensureArraysAreMutable(nodeClone));
        parentStep.children.forEach((child, index) => setSiblingOrder(child, index));
      }
    }
  } else if (type === 'substep') {
    const parentStep = findTargetStep(parentStepId);
    if (parentStep) {
      if (!Array.isArray(parentStep.substeps)) parentStep.substeps = [];
      // Check if substep already exists
      const substepId = id || nodeClone?.tree_node_id || nodeClone?.substep_id_ref;
      const existingSubstepIndex = parentStep.substeps.findIndex(substep =>
        substep.tree_node_id === substepId || substep.substep_id_ref === substepId
      );
      if (existingSubstepIndex === -1) {
        // Ensure the substep is empty (no children)
        if (nodeClone) {
          delete nodeClone.children;
        }
        parentStep.substeps.push(ensureArraysAreMutable(nodeClone));
        parentStep.substeps.forEach((substep, index) => setSiblingOrder(substep, index));
      } else {
        // Substep already exists, ensure it has no children (shouldn't happen in normal flow, but handle it)
        const existingSubstep = parentStep.substeps[existingSubstepIndex];
        delete existingSubstep.children;
      }
    }
  } else if (type === 'substepChild') {
    const parentStep = findTargetStep(parentStepId);
    const parentSubstep = findSubstepInStep(parentStep, parentSubstepId);
    if (parentSubstep) {
      if (!Array.isArray(parentSubstep.children)) parentSubstep.children = [];
      // Check if child already exists to avoid duplicates
      const existingChild = parentSubstep.children.find(child => {
        const childId = getChildNodeId(child);
        return childId === id;
      });
      if (!existingChild) {
        parentSubstep.children.push(ensureArraysAreMutable(nodeClone));
        parentSubstep.children.forEach((child, index) => setSiblingOrder(child, index));
      }
    }
  }

  normalizeDocumentSiblingOrders(clonedStructure);
  return clonedStructure;
};

// Helper to extract document info
const getDocumentInfo = (doc, fallbackName = 'Document') => ({
  partNumber: doc?.partNumber || doc?.part_number || doc?.partId || fallbackName,
  revision: doc?.revision || doc?.revisionLabel || doc?.revision_id || '-',
});

// Helper to find step in sections
const findStepInSections = (sections, stepId) => {
  for (const section of sections) {
    const step = section.steps?.find(
      s => s.tree_node_id === stepId || s.step_id_ref === stepId
    );
    if (step) return { section, step };
  }
  return null;
};

// Helper to find final step after copy/move
const findFinalStep = (newContent, sourceStep, position, movePosition, isCopyOperation = false) => {
  const sourceStepId = sourceStep.tree_node_id || sourceStep.step_id_ref;
  
  // For copy operations with 'into' position, use the target section from movePosition
  if (isCopyOperation && position === 'into' && movePosition?.targetId) {
    const targetSection = newContent.find(
      s => s.tree_node_id === movePosition.targetId || s.section_id_ref === movePosition.targetId
    );
    if (targetSection) {
      if (!targetSection.steps) return null;
      
      // Find the copied step in the target section (with dummy tree_node_id)
      const copiedStep = targetSection.steps.find(step =>
        (step.tree_node_id === sourceStepId || step.step_id_ref === sourceStepId) &&
        step.tree_node_id && step.tree_node_id.startsWith('copy-')
      );
      if (copiedStep) {
        return { section: targetSection, step: copiedStep };
      }
      
      // If not found by dummy ID, find by step_id_ref and sibling_order
      const matchingSteps = targetSection.steps.filter(step =>
        step.step_id_ref === sourceStep.step_id_ref
      );
      if (matchingSteps.length > 1) {
        // Multiple steps with same step_id_ref - the copy will have higher sibling_order
        const sorted = [...matchingSteps].sort((a, b) => {
          const orderA = getSiblingOrderNumber(a);
          const orderB = getSiblingOrderNumber(b);
          return orderB - orderA; // Descending - get the one with highest order (the copy)
        });
        return { section: targetSection, step: sorted[0] };
      }
      
      // If only one match or no match, return the last step in the target section (the newly added one)
      if (targetSection.steps.length > 0) {
        const sortedSteps = [...targetSection.steps].sort((a, b) => {
          return getSiblingOrderNumber(a) - getSiblingOrderNumber(b);
        });
        return { section: targetSection, step: sortedSteps[sortedSteps.length - 1] };
      }
    }
  }
  
  // For same-document copy operations with 'above' or 'below' position
  if (isCopyOperation && (position === 'above' || position === 'below') && movePosition?.targetId) {
    // First, find the target step (the reference step) to determine which section it's in
    const targetStepMatch = findStepInSections(newContent, movePosition.targetId);
    if (targetStepMatch) {
      const targetSection = targetStepMatch.section;
      if (targetSection?.steps) {
        // Find the copied step in the same section as the target step
        // The copied step will have:
        // 1. A dummy tree_node_id starting with "copy-"
        // 2. The same step_id_ref as the source step
        const sourceStepIdRef = sourceStep.step_id_ref;
        const copiedStep = targetSection.steps.find(step =>
          step.step_id_ref === sourceStepIdRef &&
          step.tree_node_id && step.tree_node_id.startsWith('copy-')
        );
        if (copiedStep) {
          return { section: targetSection, step: copiedStep };
        }
        
        // If not found by dummy ID, find by step_id_ref and sibling_order
        // For cross-section copies, there should only be one step with this step_id_ref in the target section
        const matchingSteps = targetSection.steps.filter(step =>
          step.step_id_ref === sourceStep.step_id_ref
        );
        if (matchingSteps.length > 0) {
          // If multiple matches, the copy will have higher sibling_order (was added later)
          // If only one match, it's the copy (original is in a different section)
          const sorted = [...matchingSteps].sort((a, b) => {
            const orderA = getSiblingOrderNumber(a);
            const orderB = getSiblingOrderNumber(b);
            return orderB - orderA; // Descending - get the one with highest order (the copy)
          });
          return { section: targetSection, step: sorted[0] };
        }
      }
    }
  }
  
  // Fallback: For same-document copy operations (if above/below logic didn't find it)
  if (isCopyOperation) {
    for (const section of newContent) {
      if (!section.steps) continue;
      
      // First, try to find the step with dummy tree_node_id (the copy)
      const copiedStep = section.steps.find(step =>
        (step.tree_node_id === sourceStepId || step.step_id_ref === sourceStepId) &&
        step.tree_node_id && step.tree_node_id.startsWith('copy-')
      );
      if (copiedStep) {
        return { section, step: copiedStep };
      }
      
      // If not found by dummy ID, find by position and sibling_order
      // For same-document copies, the copied step will have the same step_id_ref but different tree_node_id
      const matchingSteps = section.steps.filter(step =>
        step.step_id_ref === sourceStep.step_id_ref
      );
      if (matchingSteps.length > 1) {
        // Multiple steps with same step_id_ref - the copy will have higher sibling_order
        const sorted = [...matchingSteps].sort((a, b) => {
          const orderA = getSiblingOrderNumber(a);
          const orderB = getSiblingOrderNumber(b);
          return orderB - orderA; // Descending - get the one with highest order (the copy)
        });
        return { section, step: sorted[0] };
      }
    }
  }
  
  // Try to find by matching tree_node_id first (for same-document move operations)
  const match = findStepInSections(newContent, sourceStepId);
  if (match) return match;
  
  // For copy operations, find by position (fallback)
  if (position === 'into' && movePosition?.targetId) {
    const targetSection = newContent.find(
      s => s.tree_node_id === movePosition.targetId || s.section_id_ref === movePosition.targetId
    );
    if (targetSection?.steps?.length > 0) {
      const sortedSteps = [...targetSection.steps].sort((a, b) => {
        return getSiblingOrderNumber(a) - getSiblingOrderNumber(b);
      });
      return { section: targetSection, step: sortedSteps[sortedSteps.length - 1] };
    }
  }
  
  if ((position === 'above' || position === 'below') && movePosition?.targetId) {
    for (const section of newContent) {
      const targetStepIndex = section.steps?.findIndex(
        s => s.tree_node_id === movePosition.targetId || s.step_id_ref === movePosition.targetId
      );
      if (targetStepIndex !== -1 && section.steps) {
        if (position === 'above' && targetStepIndex > 0) {
          return { section, step: section.steps[targetStepIndex - 1] };
        }
        if (position === 'below' && targetStepIndex < section.steps.length - 1) {
          return { section, step: section.steps[targetStepIndex + 1] };
        }
      }
    }
  }
  
  return null;
};

// Helper to collect all possible IDs for a node
const collectPossibleIds = (id, node) => {
  const possibleIds = [id].filter(Boolean);
  if (!node) return possibleIds;
  
  const idFields = ['tree_node_id', 'step_id_ref', 'substep_id_ref', 'node_id'];
  idFields.forEach(field => {
    if (node[field] && node[field] !== id) possibleIds.push(node[field]);
  });
  
  if (node.detail) {
    CHILD_NODE_ID_FIELDS.forEach(field => {
      if (node.detail[field]) possibleIds.push(node.detail[field]);
    });
  }
  
  return possibleIds;
};

// Helper to resolve original name
const resolveOriginalName = (item, originalIndexMap, finalIndexMap, childTypeCounters) => {
  const { id, node, type } = item;
  
  const defaultLabel = type === 'step' ? 'Step'
    : type === 'substep' ? 'Substep'
    : getChildTypeDisplayName(node?.type);

  const possibleIds = collectPossibleIds(id, node);
  
  // For child nodes, use counter-based naming
  if (type === 'stepChild' || type === 'substepChild') {
    const normalizedChildType = normalizeChildType(node?.type);
    if (normalizedChildType && childTypeCounters.hasOwnProperty(normalizedChildType)) {
      childTypeCounters[normalizedChildType] += 1;
      const childDisplayName = getChildTypeDisplayName(normalizedChildType);
      return `${childDisplayName} ${childTypeCounters[normalizedChildType]}`;
    }
  }
  
  // Try to find in index maps
  const mappedId = possibleIds.find(id => id && (originalIndexMap[id] || finalIndexMap[id]));
  if (mappedId) {
    return originalIndexMap[mappedId] || finalIndexMap[mappedId];
  }
  
  return resolveNodeNameWithFallback(id, originalIndexMap, node, defaultLabel);
};

// Shared logic for building node indexing
const buildNodeIndexingCore = ({
  sourceStep,
  sourceAllNodes,
  newContent,
  targetAllNodes = [],
  targetDoc,
  sourceDoc,
  position,
  movePosition,
  actionType, // 'Move' or 'Copy'
  buildActionPrefix,
}) => {
  if (!sourceStep || !Array.isArray(newContent) || newContent.length === 0) {
    return null;
  }

  const originalIndexMap = generateNodeIndexing(sourceAllNodes || []);
  const finalIndexMap = generateNodeIndexing(newContent);
  
  const isCopyOperation = actionType === 'Copy';
  const isMoveOperation = actionType === 'Move';
  
  // For copy operations, generate index map from original target structure
  const targetIndexMap = isCopyOperation && targetAllNodes.length > 0
    ? generateNodeIndexing(targetAllNodes)
    : finalIndexMap;
  
  const finalMatch = findFinalStep(newContent, sourceStep, position, movePosition, isCopyOperation);
  if (!finalMatch) return null;
  
  const { section: finalSection, step: finalStep } = finalMatch;

  const finalSectionName = finalIndexMap[finalSection.tree_node_id] ||
    resolveNodeNameWithFallback(finalSection.tree_node_id, originalIndexMap, finalSection, 'Section');
    
  const finalStepName = finalIndexMap[finalStep.tree_node_id] ||
    resolveNodeNameWithFallback(finalStep.tree_node_id, originalIndexMap, finalStep, 'Step');

  let targetStepFinalName = '';
  if (position !== 'into' && movePosition?.targetId) {
    // For copy operations, use targetAllNodes (original structure) to find the target step
    // This ensures we get the correct step name before the copy operation changed the structure
    const targetNodesToSearch = isCopyOperation ? targetAllNodes : newContent;
    const targetMatch = findStepInSections(targetNodesToSearch, movePosition.targetId);
    if (targetMatch) {
      // For copy operations, use movePosition.globalStepNumber if available (the step number selected by user)
      // Otherwise use targetIndexMap to get the step name from the original structure
      if (isCopyOperation && movePosition?.globalStepNumber !== null && movePosition?.globalStepNumber !== undefined) {
        // Use the global step number that was selected by the user
        targetStepFinalName = `Step ${movePosition.globalStepNumber}`;
      } else {
        // Use the appropriate index map based on operation type
        const indexMapToUse = isCopyOperation ? targetIndexMap : finalIndexMap;
        targetStepFinalName = indexMapToUse[targetMatch.step.tree_node_id] ||
          resolveNodeNameWithFallback(targetMatch.step.tree_node_id, isCopyOperation ? targetIndexMap : originalIndexMap, targetMatch.step, 'Step');
      }
    }
  }

  const originalStepName = resolveNodeNameWithFallback(
    sourceStep.tree_node_id || sourceStep.step_id_ref,
    originalIndexMap,
    sourceStep,
    'Step'
  );

  const targetInfo = getDocumentInfo(targetDoc, 'Target Document');
  const sourceInfo = sourceDoc ? getDocumentInfo(sourceDoc, 'Source Document') : null;
  
  const sectionDescriptor = finalSectionName || 'the target section';
  const referenceStepName = targetStepFinalName || finalStepName || 'the target step';
  const destinationBase = `${targetInfo.partNumber}, Rev ${targetInfo.revision}`;
  
  const actionPrefix = buildActionPrefix(
    originalStepName, 
    position, 
    sectionDescriptor, 
    referenceStepName, 
    destinationBase, 
    sourceInfo, 
    targetInfo
  );

  const hierarchyNodes = collectStepHierarchyNodes(sourceStep);
  if (hierarchyNodes.length === 0) return null;

  const childTypeCounters = { attachment: 0, canvas: 0, 'rich-text': 0, video: 0, form: 0 };
  // Filter out nodes without IDs to get accurate total operations count
  const validNodes = hierarchyNodes.filter(item => item.id);
  const totalOperations = validNodes.length;
  const nodeIndexing = {};

  const baseStructure = cloneStructureForIndexing(Array.isArray(targetAllNodes) ? targetAllNodes : []);
  const initialStructure = isMoveOperation
    ? setBlankStep(baseStructure, sourceStep)
    : baseStructure;

  let currentStructure =isMoveOperation ? structuredClone(initialStructure).map(item => {
      if(item.steps){
        item.steps = item.steps.filter(step=>step.version_id_ref !== sourceStep.version_id_ref)
      }
      return item;
    }) : structuredClone(initialStructure);
  
  // Track the new copied step's tree_node_id for same-document copies
  let newCopiedStepTreeNodeId = null;
  
  // Track the previous newContent state for copy operations
  // originalContent for each operation should be the newContent from the previous operation
  let previousNewContent = null;

  // Use a separate counter for operation numbers that only increments when we create an entry
  const elementTitles = generateTitlesFromStructure(sourceAllNodes);
  let operationCounter = 0;
    
  hierarchyNodes.forEach((item, index) => {
    const { id, node, type, parentSubstepId } = item;
    if (!id) return;

    operationCounter++; // Increment only when we process a valid node

    const originalName = elementTitles[node.tree_node_id];

    const destinationName = formatDestinationName({
      type,
      finalSectionName: sectionDescriptor,
      finalStepName,
      finalIndexMap,
      parentSubstepId,
    });

    const movementPhrase = actionType === 'Copy' 
      ? `in ${destinationName} in ${destinationBase}`
      : `in ${destinationName}`;

    const title = `${actionPrefix} System Performs Operation ${operationCounter} of ${totalOperations}, ${actionType} ${originalName} ${movementPhrase}.`;

    // For copy operations, originalContent should be the previous newContent (state after previous operation)
    // For the first operation, use currentStructure (state before adding the step)
    let originalContent = null;
    if (isCopyOperation) {
      if(previousNewContent){
        originalContent = JSON.parse(JSON.stringify(previousNewContent));
      }else {
        originalContent = structuredClone(currentStructure || []);
      }
    }
    
    // Track the new copied step's tree_node_id when we add it in the first operation
    // Do this BEFORE adding children so we can use it to find the correct step
    if (isCopyOperation && type === 'step') {
      // The step will be added in addNodeToStepStructure, so we'll track it after
      // For now, we'll find it after the structure is updated
    }
    
    // Add the node to structure
    currentStructure = addNodeToStepStructure({
      currentStructure,
      hierarchyNode: item,
      finalSection,
      finalStep,
      position,
      movePosition,
      isCopyOperation,
      sourceStep,
      newCopiedStepTreeNodeId, // Pass the tracked ID so we can find the correct step
    });
    
    // Track the new copied step's tree_node_id when we add it in the first operation
    // Do this AFTER adding the step so we can use it for subsequent operations
    if (isCopyOperation && type === 'step') {
      // Find the newly added step in currentStructure by looking for the dummy tree_node_id
      const sourceStepIdRef = sourceStep.step_id_ref;
      const sectionId = finalSection?.tree_node_id || finalSection?.section_id_ref;
      const targetSection = currentStructure.find(sec => 
        (sec.tree_node_id === sectionId || sec.section_id_ref === sectionId)
      );
      if (targetSection?.steps) {
        const addedStep = targetSection.steps.find(step => 
          step.step_id_ref === sourceStepIdRef &&
          step.tree_node_id && step.tree_node_id.startsWith('copy-')
        );
        if (addedStep && addedStep.tree_node_id) {
          newCopiedStepTreeNodeId = addedStep.tree_node_id;
        }
      }
    }
    
    const newContentState = structuredClone(currentStructure || []);

    if(!isCopyOperation){
      if(index === 0) {
        originalContent = structuredClone(initialStructure);
      }else{
        originalContent = JSON.parse(JSON.stringify(previousNewContent)).map(item => {
          if(item.steps){
            item.steps = item.steps.map(step => {
              if(step.version_id_ref === sourceStep.version_id_ref) {
                newContentState.forEach(item => {
                  if(item.steps){
                    item.steps.forEach(newstep => {
                      if(newstep.version_id_ref === sourceStep.version_id_ref) {
                        step = newstep;
                      }
                    });
                  }
                });
              }
              return step;
            });
          }
          return item;
        });
      }
    }
    
    nodeIndexing[id] = {
      title,
      originalContent: originalContent || [],
      newContent: newContentState || [],
    };

    // Update previousNewContent for the next operation (for copy operations)
    // Store the newContentState so it can be used as originalContent for the next operation
    if(isCopyOperation) {
      previousNewContent = JSON.parse(JSON.stringify(newContentState || []));
    }else{
      previousNewContent = JSON.parse(JSON.stringify(originalContent || []));
    }
   
  });

  return Object.keys(nodeIndexing).length ? nodeIndexing : null;
};

export const buildNodeIndexingForStepMove = ({
  sourceStep,
  sourceAllNodes,
  newContent,
  targetDoc,
  position,
  movePosition,
  action_to_show,
  targetAllNodes = [],
}) => {
  return buildNodeIndexingCore({
    sourceStep,
    sourceAllNodes,
    newContent,
    targetAllNodes,
    targetDoc,
    sourceDoc: null,
    position,
    movePosition,
    actionType: 'Move',
    buildActionPrefix: ()=>{return action_to_show+"."},
  });
};

export const buildNodeIndexingForStepCopy = ({
  sourceStep,
  sourceAllNodes,
  newContent,
  targetDoc,
  sourceDoc,
  position,
  movePosition,
  targetAllNodes = [],
}) => {
  // Find source section that contains the source step
  const originalIndexMap = generateNodeIndexing(sourceAllNodes || []);
  let sourceSection = null;
  for (const section of sourceAllNodes || []) {
    const step = section.steps?.find(
      s => s.tree_node_id === sourceStep.tree_node_id || s.step_id_ref === sourceStep.step_id_ref
    );
    if (step) {
      sourceSection = section;
      break;
    }
  }
  
  const sourceSectionName = sourceSection 
    ? (originalIndexMap[sourceSection.tree_node_id] || 
       originalIndexMap[sourceSection.section_id_ref] || 
       `Section ${((sourceAllNodes || []).findIndex(s => s.tree_node_id === sourceSection.tree_node_id) + 1)}`)
    : 'the source section';
  
  return buildNodeIndexingCore({
    sourceStep,
    sourceAllNodes,
    newContent,
    targetAllNodes,
    targetDoc,
    sourceDoc,
    position,
    movePosition,
    actionType: 'Copy',
    buildActionPrefix: (originalStepName, position, sectionDescriptor, referenceStepName, destinationBase, sourceInfo, targetInfo) => {
      const sourceDocInfo = sourceInfo ? `from ${sourceInfo.partNumber}, Rev ${sourceInfo.revision}` : '';
      
      const positionDescriptor = position === 'into'
        ? `into ${sectionDescriptor} of ${destinationBase}`
        : position === 'above'
        ? `above ${referenceStepName} in ${sectionDescriptor} of ${destinationBase}`
        : `below ${referenceStepName} in ${sectionDescriptor} of ${destinationBase}`;
      
      return `User Copied ${originalStepName} from ${sourceSectionName} ${sourceDocInfo}, ${positionDescriptor}.`;
    },
  });
};

/**
 * Calculate global step number for a step across all sections
 */
const calculateGlobalStepNumber = (allNodes, step) => {
  if (!allNodes || !step) return 1;
  
  const sortedSections = [...allNodes].sort((a, b) => {
    return getSiblingOrderNumber(a) - getSiblingOrderNumber(b);
  });
  
  const sectionIndex = sortedSections.findIndex(
    s => s.section_id_ref === step.section_id_ref
  );
  
  if (sectionIndex === -1) return 1;
  
  const stepsBeforeThisSection = sortedSections
    .slice(0, sectionIndex)
    .reduce((total, section) => total + (section.steps?.length || 0), 0);
  
  const section = sortedSections[sectionIndex];
  const steps = section.steps || [];
  const sortedSteps = [...steps].sort((a, b) => {
    return getSiblingOrderNumber(a) - getSiblingOrderNumber(b);
  });
  
  const stepIndex = sortedSteps.findIndex(
    s => s.step_id_ref === step.step_id_ref || s.tree_node_id === step.tree_node_id
  );
  
  return stepIndex === -1 ? stepsBeforeThisSection + 1 : stepsBeforeThisSection + stepIndex + 1;
};

export const calculateStepMoveParameters = ({
  position,
  targetAllNodes,
  targetDoc,
  movePosition,
  moveCreateCopy,
  sourceStep,
  moveDocument,
}) => {
  if (position === 'into') {
    const targetSection = targetAllNodes.find(
      s => s.tree_node_id === movePosition.targetId || s.section_id_ref === movePosition.targetId
    );
    if (!targetSection) throw new Error('Target section not found.');

    const targetSteps = targetAllNodes.find(s => s.section_id_ref === targetSection.section_id_ref)?.steps || [];
    const allOrders = targetSteps
      .map(step => Number(step.sibling_order || step.procedure_tree?.sibling_order || 0))
      .filter(order => !isNaN(order));

    const toOrder = allOrders.length > 0 ? Math.max(...allOrders) + 1 : 1;
    
    const sortedSteps = [...targetSteps].sort((a, b) => {
      return getSiblingOrderNumber(a) - getSiblingOrderNumber(b);
    });
    
    return {
      toOrder,
      toProcedureTreeNodeRef: sortedSteps.length > 0 ? sortedSteps[sortedSteps.length - 1].tree_node_id : targetSection.tree_node_id,
      toSectionIdRef: targetSection.section_id_ref,
      toStepIdRef: null,
      direction: moveCreateCopy ? 'paste_down' : 'move_down',
      direction_move_into: moveCreateCopy ? undefined : true,
    };
  }

  // For "above" or "below", target is a step
  const targetMatch = findStepInSections(targetAllNodes, movePosition.targetId);
  if (!targetMatch) throw new Error('Target step not found.');

  const { section: targetSection, step: targetStep } = targetMatch;
  const targetOrder = getSiblingOrderNumber(targetStep);
  const sourceOrder = sourceStep ? getSiblingOrderNumber(sourceStep) : null;
  
  const isSameDocumentMove = !moveCreateCopy && sourceStep && moveDocument &&
    moveDocument.partId === targetDoc.partId && moveDocument.revisionId === targetDoc.revisionId;
  const isSameSection = sourceStep?.section_id_ref === targetSection.section_id_ref;

  let toOrder;
  if (isSameDocumentMove && isSameSection && sourceOrder !== null) {
    if (sourceOrder < targetOrder) {
      toOrder = position === 'above' ? targetOrder - 1 : targetOrder;
    } else {
      toOrder = position === 'above' ? targetOrder : targetOrder + 1;
    }
  } else {
    toOrder = position === 'above' ? targetOrder : targetOrder + 1;
  }
  
  return {
    toOrder,
    toProcedureTreeNodeRef: targetStep.tree_node_id,
    toSectionIdRef: targetSection.section_id_ref,
    toStepIdRef: targetStep.step_id_ref,
    direction: moveCreateCopy
      ? (position === 'above' ? 'paste_up' : 'paste_down')
      : (position === 'above' ? 'move_up' : 'move_down'),
    direction_move_into: moveCreateCopy ? undefined : (isSameSection ? false : true),
  };
};

export const prepareStepMoveNewContent = ({
  targetAllNodes,
  sourceStep,
  moveDocument,
  targetDoc,
  position,
  movePosition,
  toSectionIdRef,
  moveCreateCopy,
}) => {
  const newContent = ensureArraysAreMutable(structuredClone(targetAllNodes));
  const sourceSectionIdRef = sourceStep?.section_id_ref;
  const clonedStep = ensureArraysAreMutable(structuredClone(sourceStep));

  if (position === 'into') {
    const targetSection = newContent.find(s => s.section_id_ref === toSectionIdRef);
    if (targetSection) {
      if (moveCreateCopy) {
        targetSection.steps = [...(targetSection.steps || []), clonedStep];
      } else {
        const isSameDocument = moveDocument.partId === targetDoc.partId && 
                               moveDocument.revisionId === targetDoc.revisionId;
        
        if (isSameDocument && sourceSectionIdRef) {
          const sourceSection = newContent.find(s => s.section_id_ref === sourceSectionIdRef);
          if (sourceSection?.steps) {
            sourceSection.steps = [...sourceSection.steps].filter(
              s => s.step_id_ref !== sourceStep.step_id_ref && s.tree_node_id !== sourceStep.tree_node_id
            );
          }
        }
        targetSection.steps = [...(targetSection.steps || []), clonedStep];
      }
    }
  } else {
    const targetMatch = findStepInSections(newContent, movePosition.targetId);
    if (targetMatch) {
      const { section: targetSection, step: targetStep } = targetMatch;
      const targetIndex = targetSection.steps?.findIndex(
        s => s.tree_node_id === movePosition.targetId || s.step_id_ref === movePosition.targetId
      );

      if (targetIndex !== -1) {
        const isSameDocument = moveDocument.partId === targetDoc.partId && 
                               moveDocument.revisionId === targetDoc.revisionId;
        const isSameSection = sourceSectionIdRef === targetSection.section_id_ref;

        targetSection.steps = targetSection.steps ? [...targetSection.steps] : [];

        if (moveCreateCopy) {
          targetSection.steps.splice(position === 'above' ? targetIndex : targetIndex + 1, 0, clonedStep);
        } else {
          if (isSameDocument && sourceSectionIdRef) {
            const sourceSection = newContent.find(s => s.section_id_ref === sourceSectionIdRef);
            if (sourceSection?.steps) {
              if (!isSameSection) sourceSection.steps = [...sourceSection.steps];
              
              const currentStepsArray = isSameSection ? targetSection.steps : sourceSection.steps;
              const sourceIndex = currentStepsArray.findIndex(
                s => s.step_id_ref === sourceStep.step_id_ref && s.tree_node_id === sourceStep.tree_node_id
              );
              
              if (sourceIndex !== -1) {
                if (isSameSection) {
                  targetSection.steps.splice(sourceIndex, 1);
                  let adjustedTargetIndex = targetIndex;
                  if (sourceIndex < targetIndex) adjustedTargetIndex = targetIndex - 1;
                  targetSection.steps.splice(position === 'above' ? adjustedTargetIndex : adjustedTargetIndex + 1, 0, clonedStep);
                } else {
                  sourceSection.steps.splice(sourceIndex, 1);
                  targetSection.steps.splice(position === 'above' ? targetIndex : targetIndex + 1, 0, clonedStep);
                }
              } else {
                targetSection.steps.splice(position === 'above' ? targetIndex : targetIndex + 1, 0, clonedStep);
              }
            }
          } else {
            targetSection.steps.splice(position === 'above' ? targetIndex : targetIndex + 1, 0, clonedStep);
          }
        }
      }
    }
  }

  normalizeDocumentSiblingOrders(newContent);
  return newContent;
};

export const buildStepCopyVersionContent = ({
  action_to_show,
  allRevisionNodes,
  newContent,
  moveType,
  revision,
  status,
  type,
  partNumber,
  targetSectionIndex,
  item_code_type
}) => {
  const mappedMoveType = moveType === 'above' ? 'up' : moveType;
  return {
    action_to_show,
    moveType: mappedMoveType,
    rev: revision,
    status,
    type,
    item_code_type: item_code_type,
    partNumber,
    move_copy_action: `Section ${targetSectionIndex + 1} in ${partNumber}, Rev ${revision}`,
    main_entry: true,
  };
};

export const buildStepMoveVersionContent = async({
  action_to_show,
  allRevisionNodes,
  newContent,
  moveType,
  revision,
  status,
  type,
  item_code_type,
  partNumber,
  partDescription,
  targetSectionIndex,
  parentVersion,
}) => {
  const mappedMoveType = moveType === 'above' ? 'up' : moveType;
  return {
    action_to_show,
    moveType: mappedMoveType,
    rev: revision,
    status,
    type,
    item_code_type: item_code_type,
    partNumber,
    partDescription,
    move_copy_action: `Section ${targetSectionIndex + 1} in ${partNumber}, Rev ${revision}`,
    main_entry: true,
  };
};

const cleanupPayload = (payload) => {
  const cleaned = { ...payload };
  Object.keys(cleaned).forEach(key => {
    if (cleaned[key] === undefined) delete cleaned[key];
  });
  return cleaned;
};

export const cleanupStepCopyPayload = cleanupPayload;
export const cleanupStepMovePayload = cleanupPayload;

// Helper to build action_to_show for step operations
const buildStepActionToShow = (sourceStep, targetAllNodes, targetDoc, position, movePosition, isCopy = false) => {
  const sourceGlobalStepNumber = calculateGlobalStepNumber(targetAllNodes, sourceStep);
  const action = isCopy ? 'Copied' : 'Moved';
  
  if (position === 'into') {
    const sectionIndex = movePosition.targetIndex ?? movePosition.targetSectionIndex ?? movePosition.sectionIndex;
    return `${action} Step ${sourceGlobalStepNumber} into Section ${sectionIndex !== undefined ? sectionIndex + 1 : ''} in ${targetDoc.partNumber}, Rev ${targetDoc.revision}`;
  }
  
  const positionText = position === 'above' ? 'above' : 'below';
  const sectionIndex = movePosition.targetSectionIndex ?? movePosition.sectionIndex;
  const targetStepNumber = movePosition.globalStepNumber ?? (movePosition.targetIndex !== undefined ? movePosition.targetIndex + 1 : '');
  return `${action} Step ${sourceGlobalStepNumber} ${positionText} Step ${targetStepNumber} in Section ${sectionIndex !== undefined ? sectionIndex + 1 : ''} in ${targetDoc.partNumber}, Rev ${targetDoc.revision}`;
};

export const buildStepCopyPayload = async ({
  sourceStep,
  moveDocument,
  targetDoc,
  position,
  movePosition,
  toOrder,
  toProcedureTreeNodeRef,
  toSectionIdRef,
  toStepIdRef,
  direction,
  direction_move_into,
  moveComment,
  targetAllNodesOriginal,
  newContent,
  sourceAllNodes,
}) => {

  const versionContent = buildStepCopyVersionContent({
    action_to_show:"",
    allRevisionNodes: targetAllNodesOriginal,
    newContent,
    moveType: position,
    revision: targetDoc.revision,
    status: targetDoc.status || 'Not Submitted',
    item_code_type: targetDoc.type || 'Document',
    partNumber: targetDoc.partNumber,
    targetSectionIndex: movePosition.targetSectionIndex ?? movePosition.sectionIndex ?? 0,
  });

  const parent_version = sourceStep.version_id_ref || null;
  const audit_trail_data = await getAuditTrailData({
    action: 'STEP_COPIED',
    comment: moveComment,
    parent_version,
    version_content: versionContent,
  }, false);
  
  audit_trail_data.hash = '';
  
  // Generate node_indexing for copy operation
  const nodeIndexing = buildNodeIndexingForStepCopy({
    sourceStep,
    sourceAllNodes: sourceAllNodes || [],
    newContent: newContent || [],
    targetAllNodes: targetAllNodesOriginal || [],
    targetDoc,
    sourceDoc: moveDocument,
    position,
    movePosition,
  });


  audit_trail_data.version_content.action_to_show = Object.values(nodeIndexing)[0]?.title?.split("System Performs Operation")[0].trim();

  const payload = {
    from_procedure_tree_node_ref: sourceStep.tree_node_id,
    from_order: getSiblingOrderNumber(sourceStep),
    from_type: 'step',
    from_part_id_ref: moveDocument.partId,
    from_procedure_rev_ref: moveDocument.revisionId,
    from_section_id_ref: sourceStep.section_id_ref,
    from_step_id_ref: sourceStep.step_id_ref,
    from_substep_id_ref: null,
    to_procedure_tree_node_ref: toProcedureTreeNodeRef,
    to_order: toOrder,
    to_type: 'step',
    to_part_id_ref: targetDoc.partId,
    to_procedure_rev_ref: targetDoc.revisionId,
    to_section_id_ref: toSectionIdRef,
    to_step_id_ref: toStepIdRef,
    to_substep_id_ref: null,
    direction,
    direction_move_into: position === 'into' ? true : (direction_move_into ?? true),
    audit_trail_data,
  };

  if (nodeIndexing && Object.keys(nodeIndexing).length > 0) {
    payload.node_indexing = nodeIndexing;
  }

  return cleanupStepCopyPayload(payload);
};

export const buildStepMovePayload = async ({
  sourceStep,
  moveDocument,
  targetDoc,
  position,
  movePosition,
  moveNode,
  toOrder,
  toProcedureTreeNodeRef,
  toSectionIdRef,
  toStepIdRef,
  direction,
  direction_move_into,
  moveComment,
  targetAllNodesOriginal,
  newContent,
  sourceAllNodes,
}) => {
  const parent_version = sourceStep.version_id_ref || null;

  let action_to_show = buildStepActionToShow(sourceStep, targetAllNodesOriginal, targetDoc, position, movePosition, false);
  if (!action_to_show.startsWith('User ')) {
    action_to_show = action_to_show.startsWith('Moved ')
      ? action_to_show.replace(/^Moved\s+/, 'User Moved ')
      : `User Moved ${action_to_show}`;
  }

  const versionContent = await buildStepMoveVersionContent({
    action_to_show,
    allRevisionNodes: targetAllNodesOriginal,
    newContent,
    moveType: position,
    revision: targetDoc.revision,
    status: targetDoc.status || 'Not Submitted',
    item_code_type: targetDoc.type || 'Document',
    partNumber: targetDoc.partNumber,
    partDescription: targetDoc.description || '',
    targetSectionIndex: movePosition.targetSectionIndex ?? movePosition.sectionIndex ?? 0,
    parentVersion: parent_version,
  });

  const isDifferentSectionMove = sourceStep.section_id_ref && toSectionIdRef &&
    sourceStep.section_id_ref !== toSectionIdRef;

  const audit_trail_data = await getAuditTrailData({
    action: 'STEP_MOVED',
    comment: moveComment,
    parent_version,
    version_content: versionContent,
  }, false);
  
  audit_trail_data.hash = '';

  const payload = {
    from_procedure_tree_node_ref: sourceStep.tree_node_id,
    from_order: getSiblingOrderNumber(sourceStep),
    from_type: 'step',
    from_part_id_ref: moveDocument.partId,
    from_procedure_rev_ref: moveDocument.revisionId,
    from_section_id_ref: sourceStep.section_id_ref,
    from_step_id_ref: sourceStep.step_id_ref,
    from_substep_id_ref: null,
    to_procedure_tree_node_ref: toProcedureTreeNodeRef,
    to_order: toOrder,
    to_type: 'step',
    to_part_id_ref: targetDoc.partId,
    to_procedure_rev_ref: targetDoc.revisionId,
    to_section_id_ref: toSectionIdRef,
    to_step_id_ref: toStepIdRef,
    to_substep_id_ref: null,
    direction_move_into: direction_move_into ?? false,
    direction,
    create_copy: false,
    audit_trail_data,
  };

  const nodeIndexing = buildNodeIndexingForStepMove({
    action_to_show,
    sourceStep,
    sourceAllNodes: sourceAllNodes || targetAllNodesOriginal,
    newContent,
    targetAllNodes: targetAllNodesOriginal,
    targetDoc,
    position,
    movePosition,
  });
  if (nodeIndexing && Object.keys(nodeIndexing).length > 0) {
    payload.node_indexing = nodeIndexing;
  }

  return cleanupStepMovePayload(payload);
};
